import { type FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0AvatarPerson, F0Box, F0Button, F0Dialog, F0Icon, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import { ChevronRight } from '@factorialco/f0-react/icons/app'
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns'
import { CommunityPostListDocument } from 'generated/resources/graphql'
import type { CommunityPostDataFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { getQueryFiltersFromDropdown } from 'modules/posts/components/CommunityPostList/helpers'
import { truncatePostDescription } from 'modules/posts/lib/truncatePostDescription'

import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  selectedCommunity: CommunitiesDropdown
}

function formatPostDate(date: Date): string {
  if (isToday(date) || isYesterday(date)) {
    return formatDistanceToNow(date, { addSuffix: true })
  }
  return format(date, 'PPPp')
}

function authorDisplayName(author: CommunityPostDataFragment['author']): string {
  if (!author) return ''
  const preferred = author.employee?.preferredName
  if (preferred) return preferred
  return [author.firstName, author.lastName].filter(Boolean).join(' ').trim() || ''
}

export const PinnedPostsModal: FC<Props> = ({ isOpen, onOpenChange, selectedCommunity }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    data,
    loading,
    controls: { fetchMore, hasMore, loading: loadingMore },
  } = usePaginatedQuery(CommunityPostListDocument, {
    variables: getQueryFiltersFromDropdown(selectedCommunity, { pinned: true }),
    extractConnection: (d) =>
      d.posts.postsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
    skip: !isOpen,
  })

  const edges = data?.posts.postsConnection?.edges ?? []
  const posts = edges.map((e) => e.node)

  const handlePostClick = (postId: string) => {
    onOpenChange(false)
    navigate(navigation.communities.post.id.fullPath({ id: postId }))
  }

  const items = useMemo(
    () =>
      posts.map((post) => {
        return {
          id: String(post.id),
          subtitle: truncatePostDescription(post.description) || '—',
          avatarSrc: post.author?.avatar ? buildUrl(post.author.avatar) : undefined,
          authorName: authorDisplayName(post.author),
          authorFirstName: post.author?.firstName ?? '',
          authorLastName: post.author?.lastName ?? '',
          date: formatPostDate(stringToDate(post.createdAt, 'backend_datetime').toDate()),
        }
      }),
    [posts]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      title={i18n.t('posts.communities.pinned_messages')}
      width="md"
    >
      {isOpen && (
        <>
          {loading && (
            <Stack paddingX="p-4" paddingY="p-4">
              <F0Text variant="description" content={i18n.t('common.loading')} />
            </Stack>
          )}
          {!loading && posts.length === 0 && (
            <Stack paddingX="p-4" paddingY="p-4">
              <F0Text
                variant="description"
                content={i18n.t('posts.communities.pinned_empty_title')}
              />
            </Stack>
          )}
          {!loading && items.length > 0 && (
            <F0Box>
              {items.map((item) => (
                <F0Box
                  key={item.id}
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap="md"
                  paddingX="lg"
                  paddingY="md"
                  onClick={() => handlePostClick(item.id)}
                >
                  <F0AvatarPerson
                    firstName={item.authorFirstName}
                    lastName={item.authorLastName}
                    src={item.avatarSrc}
                    size="md"
                  />
                  <F0Box display="flex" flexDirection="column" gap="xs" grow overflow="hidden">
                    <F0Box display="flex" flexDirection="row" gap="xs" alignItems="center">
                      <F0Text content={item.authorName} variant="label" />
                      <F0Text
                        content={`· ${item.date}`}
                        variant="description"
                      />
                    </F0Box>
                    <F0Text content={item.subtitle} variant="body" />
                  </F0Box>
                  <F0Icon icon={ChevronRight} size="md" />
                </F0Box>
              ))}
            </F0Box>
          )}
          {!loading && hasMore && (
            <Stack paddingX="p-4" paddingY="p-4" justifyContent="center">
              <F0Button variant="ghost" disabled={loadingMore} onClick={() => fetchMore()}>
                {loadingMore ? i18n.t('common.loading') : i18n.t('common.load_more')}
              </F0Button>
            </Stack>
          )}
        </>
      )}
    </F0Dialog>
  )
}
